
import { Suspense, useEffect, useId, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Check,
  History,
  Loader2,
  Paperclip,
  Plus,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { apiFetch } from "@/lib/apiClient";
import type {
  OneAiClarifyPrompt,
  OneAiIntakeForm,
  PendingToolConfirmation,
  SupportChatResponse,
  SupportLane,
} from "@/lib/supportAgent";
import { SUPPORT_LANE_LABELS } from "@/lib/supportAgent/lanes";
import {
  resolveOneAiPageContext,
  shouldWelcomeBack,
  welcomeBackReply,
} from "@/lib/supportAgent/pageContext";
import { useDashboardOrgHref } from "@/lib/useDashboardOrgHref";
import { OneAiMessageFeedback } from "@/components/Support/OneAiMessageFeedback";
import {
  clampOneAiPanelGeometry,
  defaultOneAiPanelGeometry,
  loadOneAiPanelGeometry,
  saveOneAiPanelGeometry,
  type OneAiPanelGeometry,
} from "@/components/Support/oneAiPanelGeometry";
import { formatAppVersionLabel, getAppVersion } from "@/lib/appVersion";

/** Shared One AI chat shell — overlay (ERP/Hub popup) or full page (ai.atw.net). */
export const SUPPORT_AGENT_OPEN_EVENT = "atw-support-agent-open";
export const ONE_AI_OPEN_EVENT = SUPPORT_AGENT_OPEN_EVENT;

type UiAttachment = {
  id: string;
  name: string;
  mime: string;
  size: number;
  kind: "image" | "file";
  previewUrl?: string;
  publicUrl?: string;
  status: "ready" | "uploading" | "error";
};

type UiMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  thinking?: string;
  lane?: SupportLane;
  creditsBurned?: number;
  deepLinks?: Array<{ label: string; href: string }>;
  attachments?: UiAttachment[];
};

export type OneAiChatVariant = "overlay" | "page";

export type OneAiChatShellProps = {
  /** App dashboard uses authenticated endpoint; marketing uses product stub. */
  mode?: "app" | "product";
  /** overlay = floating ERP panel; page = full desktop app (ai.atw.net). */
  variant?: OneAiChatVariant;
  /** Controlled open (optional). Ignored when variant is page (always open). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Force first-run bootstrap (also auto-detected from ?welcome=1). */
  firstRun?: boolean;
  /** Optional conversation id to open (page deep link /c/[id]). */
  initialConversationId?: string | null;
  /** Surface label for history metadata (one | ai | hub). */
  surface?: "one" | "ai" | "hub";
};

const OPENING_LINE = "Ask anything — I’m here to help.";
const CLIENT_APP_VERSION = formatAppVersionLabel(getAppVersion());

function endpointForMode(mode: "app" | "product"): string {
  return mode === "product" ? "/api/support-agent/product/chat" : "/api/support-agent/chat";
}

export function OneAiChatShell(props: OneAiChatShellProps) {
  return (
    <Suspense fallback={null}>
      <OneAiChatShellInner {...props} />
    </Suspense>
  );
}

/** @deprecated Use OneAiChatShell — kept for call sites during migration. */
export function SupportAgentBubble(props: OneAiChatShellProps) {
  return <OneAiChatShell {...props} />;
}

function OneAiChatShellInner({
  mode = "app",
  variant = "overlay",
  open: controlledOpen,
  onOpenChange,
  firstRun: firstRunProp,
  initialConversationId = null,
  surface = "one",
}: OneAiChatShellProps) {
  const panelId = useId();
  const titleId = useId();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const orgHref = useDashboardOrgHref();
  const welcome = searchParams.get("welcome") === "1";
  const firstRun = firstRunProp === true || (mode === "app" && welcome);

  const pageCtx = resolveOneAiPageContext(pathname);
  const lastActiveKey = "atw-one-ai-last-active";

  const [uncontrolledOpen, setUncontrolledOpen] = useState(variant === "page");
  const open = variant === "page" ? true : (controlledOpen ?? uncontrolledOpen);

  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(pageCtx.suggestions);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingToolConfirmation | null>(
    null,
  );
  const [intakeForm, setIntakeForm] = useState<OneAiIntakeForm | null>(null);
  const [intakeValues, setIntakeValues] = useState<Record<string, string>>({});
  const [intakeLogoBusy, setIntakeLogoBusy] = useState(false);
  const [clarifyPrompt, setClarifyPrompt] = useState<OneAiClarifyPrompt | null>(null);
  const [clarifyOtherOpen, setClarifyOtherOpen] = useState(false);
  const [clarifyOtherText, setClarifyOtherText] = useState("");
  const [composerAttachments, setComposerAttachments] = useState<UiAttachment[]>([]);
  const [attachBusy, setAttachBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState<
    Array<{
      id: string;
      title: string;
      updatedAt: string;
      preview?: string;
      pageModule?: string | null;
      surface?: string | null;
    }>
  >([]);
  const [liveVersionLabel, setLiveVersionLabel] = useState(CLIENT_APP_VERSION);
  const [panelGeo, setPanelGeo] = useState<OneAiPanelGeometry>(() => defaultOneAiPanelGeometry());
  const [geoReady, setGeoReady] = useState(false);
  const [portalReady, setPortalReady] = useState(false);

  const setOpen = (next: boolean) => {
    if (!next) setHistoryOpen(false);
    onOpenChange?.(next);
    if (controlledOpen === undefined) setUncontrolledOpen(next);
  };
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<{
    mode: "move" | "resize";
    startX: number;
    startY: number;
    origin: OneAiPanelGeometry;
  } | null>(null);
  const idSeq = useRef(0);
  const bootstrappedRef = useRef(false);
  const welcomeOpenedRef = useRef(false);
  const welcomeBackDoneRef = useRef(false);
  const nextId = (prefix: string) => {
    idSeq.current += 1;
    return `${prefix}-${idSeq.current}`;
  };

  useEffect(() => {
    setPanelGeo(loadOneAiPanelGeometry());
    setGeoReady(true);
    setPortalReady(true);
  }, []);

  const initialConvLoadedRef = useRef(false);
  useEffect(() => {
    if (!initialConversationId || initialConvLoadedRef.current || mode !== "app") return;
    initialConvLoadedRef.current = true;
    void openConversation(initialConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once from deep link
  }, [initialConversationId, mode]);

  useEffect(() => {
    if (variant !== "page" || mode !== "app") return;
    void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- page rail once
  }, [variant, mode]);

  useEffect(() => {
    if (!geoReady) return;
    saveOneAiPanelGeometry(panelGeo);
  }, [panelGeo, geoReady]);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (drag.mode === "move") {
        setPanelGeo(
          clampOneAiPanelGeometry({
            ...drag.origin,
            left: drag.origin.left + dx,
            top: drag.origin.top + dy,
          }),
        );
      } else {
        setPanelGeo(
          clampOneAiPanelGeometry({
            ...drag.origin,
            width: drag.origin.width + dx,
            height: drag.origin.height + dy,
          }),
        );
      }
    }
    function onUp() {
      dragRef.current = null;
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  function startPanelDrag(e: ReactPointerEvent, mode: "move" | "resize") {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: panelGeo,
    };
  }

  function markActive() {
    try {
      window.localStorage.setItem(lastActiveKey, new Date().toISOString());
    } catch {
      /* ignore */
    }
  }

  async function ensureConversation(forceNew = false): Promise<string | null> {
    if (mode !== "app") return null;
    if (!forceNew && conversationId) return conversationId;
    try {
      const res = await apiFetch("/api/support-agent/conversations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create",
          pagePath: pathname,
          pageModule: pageCtx.module,
          surface,
          lastHost: typeof window !== "undefined" ? window.location.host : undefined,
        }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { conversation?: { id: string } };
      const id = data.conversation?.id || null;
      if (id) setConversationId(id);
      return id;
    } catch {
      return null;
    }
  }

  async function persistTurn(userContent: string, assistant: UiMessage, convId: string | null) {
    if (mode !== "app" || !convId) return;
    const now = new Date().toISOString();
    try {
      await apiFetch("/api/support-agent/conversations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "append",
          conversationId: convId,
          pagePath: pathname,
          pageModule: pageCtx.module,
          surface,
          lastHost: typeof window !== "undefined" ? window.location.host : undefined,
          messages: [
            ...(userContent
              ? [{ id: nextId("persist-u"), role: "user" as const, content: userContent, createdAt: now }]
              : []),
            {
              id: assistant.id,
              role: "assistant" as const,
              content: assistant.content,
              lane: assistant.lane,
              createdAt: now,
            },
          ],
        }),
      });
    } catch {
      /* best-effort */
    }
  }

  async function loadHistory() {
    if (mode !== "app") return;
    setHistoryLoading(true);
    try {
      const res = await apiFetch("/api/support-agent/conversations");
      if (!res.ok) return;
      const data = (await res.json()) as {
        conversations?: Array<{
          id: string;
          title: string;
          updatedAt: string;
          preview?: string;
          pageModule?: string | null;
          surface?: string | null;
        }>;
      };
      setHistoryItems(data.conversations || []);
    } catch {
      /* ignore */
    } finally {
      setHistoryLoading(false);
    }
  }

  async function openConversation(id: string) {
    setHistoryOpen(false);
    setPendingConfirmation(null);
    setIntakeForm(null);
    setIntakeValues({});
    setClarifyPrompt(null);
    setClarifyOtherOpen(false);
    setClarifyOtherText("");
    setComposerAttachments([]);
    setError(null);
    setPending(true);
    try {
      const res = await apiFetch(`/api/support-agent/conversations?id=${encodeURIComponent(id)}`);
      if (!res.ok) throw new Error("Could not open chat");
      const data = (await res.json()) as {
        conversation?: {
          id: string;
          messages?: Array<{ id: string; role: "user" | "assistant"; content: string; lane?: SupportLane }>;
        };
      };
      const conv = data.conversation;
      if (!conv) throw new Error("Chat not found");
      setConversationId(conv.id);
      setMessages(
        (conv.messages || []).map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          lane: m.lane,
        })),
      );
      setSuggestions(pageCtx.suggestions);
      bootstrappedRef.current = true;
      welcomeBackDoneRef.current = true;
      markActive();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open chat");
    } finally {
      setPending(false);
    }
  }

  async function startNewChat() {
    setHistoryOpen(false);
    setMessages([]);
    setPendingConfirmation(null);
    setIntakeForm(null);
    setIntakeValues({});
    setClarifyPrompt(null);
    setClarifyOtherOpen(false);
    setClarifyOtherText("");
    setComposerAttachments([]);
    setError(null);
    setSuggestions(pageCtx.suggestions);
    setConversationId(null);
    bootstrappedRef.current = false;
    welcomeBackDoneRef.current = false;
    await ensureConversation(true);
    markActive();
  }

  useEffect(() => {
    const onOpen = () => {
      onOpenChange?.(true);
      if (controlledOpen === undefined) setUncontrolledOpen(true);
    };
    window.addEventListener(SUPPORT_AGENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SUPPORT_AGENT_OPEN_EVENT, onOpen);
  }, [controlledOpen, onOpenChange]);

  // First-run: auto-open on welcome landing
  useEffect(() => {
    if (mode !== "app" || !welcome || welcomeOpenedRef.current) return;
    welcomeOpenedRef.current = true;
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per welcome visit
  }, [mode, welcome, pathname]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, pending, open, pendingConfirmation, intakeForm]);

  function appendAssistant(data: SupportChatResponse) {
    const assistant: UiMessage = {
      id: nextId("a"),
      role: "assistant",
      content: data.reply,
      thinking: data.thinking,
      lane: data.lane,
      creditsBurned: data.receipt?.creditsBurned ?? 0,
      deepLinks: data.deepLinks,
    };
    setMessages((prev) => [...prev, assistant]);
    if (data.suggestions?.length) setSuggestions(data.suggestions);
    else setSuggestions(pageCtx.suggestions);
    setPendingConfirmation(data.pendingConfirmation ?? null);
    if (data.intakeForm) {
      setIntakeForm(data.intakeForm);
      const initial: Record<string, string> = {};
      for (const f of data.intakeForm.fields) initial[f.name] = "";
      setIntakeValues(initial);
    } else {
      setIntakeForm(null);
      setIntakeValues({});
    }
    if (data.clarifyPrompt) {
      setClarifyPrompt(data.clarifyPrompt);
      setClarifyOtherOpen(false);
      setClarifyOtherText("");
    } else {
      setClarifyPrompt(null);
      setClarifyOtherOpen(false);
      setClarifyOtherText("");
    }
    if (data.appVersion?.version) {
      setLiveVersionLabel(
        formatAppVersionLabel({
          ...getAppVersion(),
          version: data.appVersion.version,
          buildId: data.appVersion.buildId || getAppVersion().buildId,
        }),
      );
    }
    return assistant;
  }

  async function uploadImageFile(file: File): Promise<string> {
    const presign = await apiFetch("/api/uploads/content-image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileType: file.type || "image/png",
        fileName: file.name,
        fileSize: file.size,
      }),
    });
    const presignJson = (await presign.json().catch(() => ({}))) as {
      uploadUrl?: string;
      publicUrl?: string;
      error?: string;
    };
    if (!presign.ok || !presignJson.uploadUrl || !presignJson.publicUrl) {
      throw new Error(presignJson.error || "Could not start upload");
    }
    const put = await fetch(presignJson.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type || "image/png" },
      body: file,
    });
    if (!put.ok) throw new Error("Upload failed");
    return presignJson.publicUrl;
  }

  async function addComposerFiles(files: FileList | File[]) {
    const list = Array.from(files).slice(0, 6);
    if (!list.length) return;
    setAttachBusy(true);
    setError(null);
    try {
      for (const file of list) {
        const id = nextId("att");
        const isImage = file.type.startsWith("image/");
        const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
        const base: UiAttachment = {
          id,
          name: file.name,
          mime: file.type || "application/octet-stream",
          size: file.size,
          kind: isImage ? "image" : "file",
          previewUrl,
          status: isImage ? "uploading" : "ready",
        };
        setComposerAttachments((prev) => [...prev, base]);
        if (isImage) {
          try {
            const publicUrl = await uploadImageFile(file);
            setComposerAttachments((prev) =>
              prev.map((a) => (a.id === id ? { ...a, publicUrl, status: "ready" } : a)),
            );
          } catch (e) {
            setComposerAttachments((prev) =>
              prev.map((a) => (a.id === id ? { ...a, status: "error" } : a)),
            );
            setError(e instanceof Error ? e.message : "Image upload failed");
          }
        }
      }
    } finally {
      setAttachBusy(false);
    }
  }

  async function uploadIntakeImage(fieldName: string, file: File) {
    setIntakeLogoBusy(true);
    setError(null);
    try {
      const publicUrl = await uploadImageFile(file);
      setIntakeValues((prev) => ({ ...prev, [fieldName]: publicUrl }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Logo upload failed");
    } finally {
      setIntakeLogoBusy(false);
    }
  }

  // Page-aware default chips when path changes
  useEffect(() => {
    if (messages.length === 0) setSuggestions(pageCtx.suggestions);
  }, [pageCtx.module, pageCtx.path, messages.length]);

  // Welcome back after long absence (app mode, not first-run)
  useEffect(() => {
    if (!open || mode !== "app" || firstRun || welcomeBackDoneRef.current || pending) return;
    if (messages.length > 0) return;
    let last: string | null = null;
    try {
      last = window.localStorage.getItem(lastActiveKey);
    } catch {
      last = null;
    }
    if (!shouldWelcomeBack(last)) {
      welcomeBackDoneRef.current = true;
      return;
    }
    welcomeBackDoneRef.current = true;
    const assistant: UiMessage = {
      id: nextId("a"),
      role: "assistant",
      content: welcomeBackReply(pageCtx),
      lane: "support",
      creditsBurned: 0,
    };
    setMessages([assistant]);
    setSuggestions(pageCtx.suggestions);
    markActive();
    void (async () => {
      const id = await ensureConversation();
      await persistTurn("", assistant, id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, firstRun]);

  // Bootstrap first-run intro when panel opens
  useEffect(() => {
    if (!open || mode !== "app" || !firstRun || bootstrappedRef.current || pending) return;
    if (messages.length > 0) return;
    bootstrappedRef.current = true;
    let cancelled = false;
    const run = async () => {
      setPending(true);
      setError(null);
      try {
        const convId = await ensureConversation();
        const res = await fetch(endpointForMode(mode), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: "",
            firstRun: true,
            pagePath: pathname,
            inOngoingConversation: false,
          }),
        });
        if (!res.ok) {
          const payload = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(payload?.error || `Request failed (${res.status})`);
        }
        const data = (await res.json()) as SupportChatResponse;
        if (!cancelled) {
          const assistant = appendAssistant(data);
          markActive();
          await persistTurn("", assistant, convId);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Something went wrong");
          bootstrappedRef.current = false;
        }
      } finally {
        if (!cancelled) setPending(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, firstRun]);

  async function sendMessage(raw: string) {
    const text = raw.trim();
    const readyAttachments = composerAttachments.filter((a) => a.status === "ready");
    if ((!text && readyAttachments.length === 0) || pending) return;
    if (composerAttachments.some((a) => a.status === "uploading")) {
      setError("Wait for uploads to finish, then send.");
      return;
    }

    const attachmentBlock =
      readyAttachments.length > 0
        ? [
            "",
            "[Attachments for review]",
            ...readyAttachments.map((a) =>
              a.kind === "image" && a.publicUrl
                ? `- image: ${a.name} → ${a.publicUrl}`
                : `- file: ${a.name} (${Math.round(a.size / 1024)} KB, ${a.mime})`,
            ),
          ].join("\n")
        : "";
    const message = `${text}${attachmentBlock}`.trim();

    setError(null);
    setInput("");
    setClarifyPrompt(null);
    setClarifyOtherOpen(false);
    setClarifyOtherText("");
    const sentAttachments = readyAttachments.map((a) => ({ ...a }));
    setComposerAttachments([]);
    const userMsg: UiMessage = {
      id: nextId("u"),
      role: "user",
      content: message,
      attachments: sentAttachments.length ? sentAttachments : undefined,
    };
    const priorCount = messages.length;
    setMessages((prev) => [...prev, userMsg]);
    setPending(true);

    try {
      const convId = await ensureConversation();
      const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
      const history = messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(-20)
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(endpointForMode(mode), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          firstRun: firstRun || undefined,
          pagePath: pathname,
          inOngoingConversation: priorCount > 0,
          lastCreditsBurned: lastAssistant?.creditsBurned ?? null,
          lastLane: lastAssistant?.lane ?? null,
          history,
        }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || `Request failed (${res.status})`);
      }
      const data = (await res.json()) as SupportChatResponse;
      const assistant = appendAssistant(data);
      markActive();
      await persistTurn(message, assistant, convId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setPending(false);
    }
  }

  async function confirmPending() {
    if (!pendingConfirmation || pending || mode !== "app") return;
    setPending(true);
    setError(null);
    try {
      const res = await apiFetch("/api/support-agent/tools/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool: pendingConfirmation.tool,
          args: pendingConfirmation.args,
          confirmed: true,
          confirmationId: pendingConfirmation.id,
        }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || `Request failed (${res.status})`);
      }
      const data = (await res.json()) as {
        reply: string;
        lane?: SupportLane;
        receipt?: { creditsBurned?: number };
        deepLinks?: Array<{ label: string; href: string }>;
        ok?: boolean;
      };
      setPendingConfirmation(null);
      setIntakeForm(null);
      setIntakeValues({});
      const assistant: UiMessage = {
        id: nextId("a"),
        role: "assistant",
        content: data.reply,
        lane: data.lane || "support",
        creditsBurned: data.receipt?.creditsBurned ?? 0,
        deepLinks: data.deepLinks,
      };
      setMessages((prev) => [...prev, assistant]);
      setSuggestions(pageCtx.suggestions);
      markActive();
      const convId = await ensureConversation();
      await persistTurn("", assistant, convId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setPending(false);
    }
  }

  function cancelPending() {
    setPendingConfirmation(null);
    setIntakeForm(null);
    setIntakeValues({});
    setMessages((prev) => [
      ...prev,
      {
        id: nextId("a"),
        role: "assistant",
        content: "No problem — nothing was written. Tell me when you want to create a lead or invoice, or ask anything else.",
        lane: "support",
        creditsBurned: 0,
      },
    ]);
  }

  function submitIntakeForm() {
    if (!intakeForm || pending) return;
    const missing = intakeForm.fields.filter(
      (f) => f.required && !String(intakeValues[f.name] || "").trim(),
    );
    if (missing.length) {
      setError(`Please fill in: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setError(null);
    const lines = [`[${intakeForm.kind === "create_lead" ? "Lead form" : "Invoice form"}]`];
    for (const f of intakeForm.fields) {
      const v = String(intakeValues[f.name] || "").trim();
      if (v) lines.push(`${f.name}: ${v}`);
    }
    setIntakeForm(null);
    setIntakeValues({});
    void sendMessage(lines.join("\n"));
  }

  const historyPanel = historyOpen && mode === "app" ? (
    <div
      className={
        variant === "page"
          ? "flex h-full w-72 shrink-0 flex-col border-r border-emerald-900/10 bg-white dark:border-white/10 dark:bg-zinc-950"
          : "absolute inset-0 z-10 flex flex-col bg-gray-100 dark:bg-zinc-800"
      }
      role="dialog"
      aria-label="Chat history"
    >
      <div className="flex items-center justify-between border-b border-emerald-900/10 px-3 py-2 dark:border-white/10">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          History
        </p>
        {variant === "overlay" ? (
          <button
            type="button"
            className="rounded-md px-2 py-1 text-[11px] font-medium text-[#0b3d2e] hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-zinc-800"
            onClick={() => setHistoryOpen(false)}
          >
            Back to chat
          </button>
        ) : (
          <button
            type="button"
            className="rounded-md px-2 py-1 text-[11px] font-medium text-[#0b3d2e] hover:bg-emerald-50 dark:text-emerald-300"
            onClick={() => void startNewChat()}
          >
            New chat
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {historyLoading ? (
          <p className="flex items-center gap-1.5 px-2 text-xs text-gray-500">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Loading…
          </p>
        ) : (
          <ul className="space-y-1">
            {historyItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`w-full rounded-lg px-2.5 py-2 text-left text-xs transition-colors hover:bg-emerald-50 dark:hover:bg-zinc-800 ${
                    conversationId === item.id
                      ? "bg-emerald-50 ring-1 ring-emerald-800/20 dark:bg-emerald-950/40 dark:ring-emerald-500/30"
                      : ""
                  }`}
                  onClick={() => void openConversation(item.id)}
                >
                  <span className="block font-medium text-gray-900 dark:text-gray-100">
                    {item.title}
                  </span>
                  {item.preview ? (
                    <span className="mt-0.5 line-clamp-2 block text-[11px] text-gray-500 dark:text-gray-400">
                      {item.preview}
                    </span>
                  ) : null}
                  <span className="mt-0.5 block text-[10px] text-gray-400">
                    {new Date(item.updatedAt).toLocaleString()}
                    {item.pageModule ? ` · ${item.pageModule}` : ""}
                  </span>
                </button>
              </li>
            ))}
            {historyItems.length === 0 ? (
              <li className="px-2 text-xs text-gray-500">No saved chats yet.</li>
            ) : null}
          </ul>
        )}
      </div>
    </div>
  ) : null;

  const chatColumn = (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {variant === "overlay" ? historyPanel : null}

      <div
        ref={listRef}
        className="flex-1 space-y-2.5 overflow-y-auto px-3 py-3 text-sm"
        aria-live="polite"
      >
        {messages.length === 0 && !pending ? (
          <div className="rounded-xl bg-white/80 px-3 py-2.5 text-gray-700 shadow-sm dark:bg-zinc-800/80 dark:text-gray-200">
            <p className="font-medium text-[#0b3d2e] dark:text-emerald-300">Hi — I’m One AI</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
              {mode === "app" ? (
                <>
                  What are you working on? You&apos;re on{" "}
                  <span className="font-medium">{pageCtx.label}</span> — ask about this, or
                  anything else in ATW One. I can help you create records, answer questions, and
                  remember how your business runs.
                </>
              ) : (
                <>What would you like to know about ATW One? Ask anything — I’m happy to help.</>
              )}
            </p>
          </div>
        ) : null}

        {messages.map((m) => (
          <div
            key={m.id}
            className={
              m.role === "user"
                ? "ml-6 rounded-xl bg-[#0b3d2e] px-3 py-2 text-white dark:bg-emerald-800"
                : "mr-4 rounded-xl bg-white px-3 py-2 text-gray-800 shadow-sm dark:bg-zinc-800 dark:text-gray-100"
            }
          >
            {m.role === "assistant" && m.thinking ? (
              <p className="mb-1.5 text-[11px] italic leading-snug text-gray-500 dark:text-gray-400">
                Thinking: {m.thinking}
              </p>
            ) : null}
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed">{m.content}</p>
            {m.attachments?.length ? (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {m.attachments.map((att) => (
                  <li key={att.id}>
                    {att.kind === "image" && (att.publicUrl || att.previewUrl) ? (
                      // eslint-disable-next-line @next/next/no-img-element -- chat attachment preview
                      <img
                        src={att.publicUrl || att.previewUrl}
                        alt={att.name}
                        className="h-16 w-16 rounded-lg object-cover ring-1 ring-white/30"
                      />
                    ) : (
                      <span className="inline-flex rounded-md bg-white/15 px-1.5 py-0.5 text-[10px]">
                        {att.name}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
            {m.role === "assistant" && m.deepLinks?.length ? (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {m.deepLinks.map((link) => (
                  <li key={link.href + link.label}>
                    <Link
                      to={orgHref(link.href)}
                      className="text-[11px] font-medium text-[#0b3d2e] underline-offset-2 hover:underline dark:text-emerald-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
            {m.role === "assistant" && m.lane ? (
              <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="font-medium text-gray-600 dark:text-gray-300">
                  Used {m.creditsBurned ?? 0} Credits
                </span>
                <span className="text-gray-400 dark:text-gray-500">
                  {" "}
                  · {SUPPORT_LANE_LABELS[m.lane]}
                </span>
              </p>
            ) : null}
            {m.role === "assistant" ? (
              <OneAiMessageFeedback
                messageId={m.id}
                replyContent={m.content}
                lane={m.lane}
                mode={mode}
              />
            ) : null}
          </div>
        ))}

        {clarifyPrompt && !pendingConfirmation && !intakeForm ? (
          <div className="mr-4 rounded-xl border border-emerald-800/20 bg-white px-3 py-2.5 shadow-sm dark:border-emerald-500/30 dark:bg-zinc-900">
            <p className="text-[13px] font-medium leading-snug text-[#0b3d2e] dark:text-emerald-200">
              {clarifyPrompt.question}
            </p>
            <div className="mt-2.5 flex flex-col gap-1.5">
              {clarifyPrompt.choices.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    setClarifyPrompt(null);
                    void sendMessage(choice.label);
                  }}
                  className="w-full rounded-lg border border-emerald-900/15 bg-[#f7faf8] px-2.5 py-2 text-left text-[12px] font-medium text-[#0b3d2e] transition-colors hover:bg-emerald-50 disabled:opacity-40 dark:border-white/15 dark:bg-zinc-800 dark:text-emerald-100 dark:hover:bg-zinc-700"
                >
                  {choice.label}
                </button>
              ))}
              {clarifyPrompt.allowOther !== false ? (
                clarifyOtherOpen ? (
                  <form
                    className="mt-0.5 flex flex-col gap-1.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const t = clarifyOtherText.trim();
                      if (!t || pending) return;
                      setClarifyPrompt(null);
                      setClarifyOtherOpen(false);
                      setClarifyOtherText("");
                      void sendMessage(t);
                    }}
                  >
                    <input
                      type="text"
                      autoFocus
                      value={clarifyOtherText}
                      disabled={pending}
                      placeholder={clarifyPrompt.otherPlaceholder || "Type your own answer…"}
                      onChange={(e) => setClarifyOtherText(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-[13px] text-gray-900 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0b3d2e] disabled:opacity-60 dark:border-white/10 dark:bg-zinc-800 dark:text-gray-100"
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={pending || !clarifyOtherText.trim()}
                        className="rounded-lg bg-[#0b3d2e] px-2.5 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40 dark:bg-emerald-800"
                      >
                        Send
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setClarifyOtherOpen(false);
                          setClarifyOtherText("");
                        }}
                        className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-[11px] font-medium dark:border-white/15"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => setClarifyOtherOpen(true)}
                    className="w-full rounded-lg border border-dashed border-emerald-900/25 px-2.5 py-2 text-left text-[12px] font-medium text-gray-600 hover:bg-emerald-50/80 dark:border-white/20 dark:text-gray-300"
                  >
                    Other…
                  </button>
                )
              ) : null}
            </div>
          </div>
        ) : null}

        {intakeForm && !pendingConfirmation ? (
          <form
            className="mr-4 rounded-xl border border-emerald-800/20 bg-white px-3 py-2.5 shadow-sm dark:border-emerald-500/30 dark:bg-zinc-900"
            onSubmit={(e) => {
              e.preventDefault();
              submitIntakeForm();
            }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#0b3d2e] dark:text-emerald-300">
              {intakeForm.title}
            </p>
            {intakeForm.description ? (
              <p className="mt-1 text-[12px] leading-relaxed text-gray-600 dark:text-gray-300">
                {intakeForm.description}
              </p>
            ) : null}
            <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {intakeForm.fields.map((field) => (
                <label
                  key={field.name}
                  className={
                    field.name === "companyName" || field.name === "companyLogoUrl"
                      ? "block sm:col-span-2"
                      : "block"
                  }
                >
                  <span className="text-[11px] font-medium text-gray-700 dark:text-gray-200">
                    {field.label}
                    {field.required ? " *" : ""}
                  </span>
                  {field.type === "image" ? (
                    <div className="mt-0.5 space-y-1.5">
                      {intakeValues[field.name] ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={intakeValues[field.name]}
                          alt=""
                          className="h-14 w-14 rounded-lg object-cover ring-1 ring-emerald-900/15"
                        />
                      ) : null}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        disabled={pending || intakeLogoBusy}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void uploadIntakeImage(field.name, file);
                          e.target.value = "";
                        }}
                        className="block w-full text-[11px] text-gray-600 file:mr-2 file:rounded-md file:border-0 file:bg-emerald-50 file:px-2 file:py-1 file:text-[11px] file:font-medium file:text-[#0b3d2e]"
                      />
                      <input
                        type="url"
                        name={field.name}
                        value={intakeValues[field.name] || ""}
                        placeholder={field.placeholder}
                        disabled={pending}
                        onChange={(e) =>
                          setIntakeValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                        }
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[13px] text-gray-900 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0b3d2e] disabled:opacity-60 dark:border-white/10 dark:bg-zinc-800 dark:text-gray-100"
                      />
                      {field.hint ? (
                        <p className="text-[10px] text-gray-500">{field.hint}</p>
                      ) : null}
                      {intakeLogoBusy ? (
                        <p className="text-[10px] text-gray-500">Uploading logo…</p>
                      ) : null}
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      name={field.name}
                      value={intakeValues[field.name] || ""}
                      placeholder={field.placeholder}
                      required={field.required}
                      disabled={pending}
                      onChange={(e) =>
                        setIntakeValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[13px] text-gray-900 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-[#0b3d2e] disabled:opacity-60 dark:border-white/10 dark:bg-zinc-800 dark:text-gray-100"
                    />
                  )}
                </label>
              ))}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={pending || intakeLogoBusy}
                className="inline-flex items-center gap-1 rounded-lg bg-[#0b3d2e] px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-[#0f4d3a] disabled:opacity-40 dark:bg-emerald-800"
              >
                {intakeLogoBusy ? "Uploading…" : intakeForm.submitLabel}
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  setIntakeForm(null);
                  setIntakeValues({});
                }}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-white/15 dark:bg-zinc-800 dark:text-gray-200"
              >
                Dismiss
              </button>
            </div>
          </form>
        ) : null}

        {pendingConfirmation ? (
          <div className="mr-4 rounded-xl border border-emerald-800/20 bg-emerald-50/90 px-3 py-2.5 dark:border-emerald-500/30 dark:bg-emerald-950/40">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#0b3d2e] dark:text-emerald-300">
              Confirm before write
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-gray-800 dark:text-gray-100">
              {pendingConfirmation.preview}
            </p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={pending}
                onClick={() => void confirmPending()}
                className="inline-flex items-center gap-1 rounded-lg bg-[#0b3d2e] px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-[#0f4d3a] disabled:opacity-40 dark:bg-emerald-800"
              >
                <Check className="size-3.5" aria-hidden />
                Confirm
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={cancelPending}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-white/15 dark:bg-zinc-800 dark:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        {pending ? (
          <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Thinking…
          </p>
        ) : null}

        {error ? (
          <p
            className="rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300"
            role="alert"
          >
            {error}
          </p>
        ) : null}
      </div>

      {suggestions.length > 0 && !pending && !intakeForm && !clarifyPrompt ? (
        <div className="flex flex-wrap gap-1.5 border-t border-emerald-900/10 px-3 py-2 dark:border-white/10">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="rounded-lg border border-emerald-900/15 bg-white px-2 py-1 text-[11px] font-medium text-[#0b3d2e] transition-colors hover:bg-emerald-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0b3d2e] dark:border-white/15 dark:bg-zinc-800 dark:text-emerald-200 dark:hover:bg-zinc-700"
              onClick={() => void sendMessage(s)}
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}

      <form
        className="flex flex-col gap-1.5 border-t border-emerald-900/10 bg-white/70 px-2 py-2 dark:border-white/10 dark:bg-zinc-900/80"
        onSubmit={(e) => {
          e.preventDefault();
          void sendMessage(input);
        }}
      >
        {composerAttachments.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5 px-0.5">
            {composerAttachments.map((att) => (
              <li
                key={att.id}
                className="relative flex items-center gap-1 rounded-lg border border-emerald-900/10 bg-white px-1.5 py-1 dark:border-white/10 dark:bg-zinc-800"
              >
                {att.kind === "image" && (att.previewUrl || att.publicUrl) ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={att.publicUrl || att.previewUrl}
                    alt=""
                    className="size-8 rounded object-cover"
                  />
                ) : (
                  <span className="max-w-[7rem] truncate text-[10px] text-gray-600 dark:text-gray-300">
                    {att.name}
                  </span>
                )}
                {att.status === "uploading" ? (
                  <Loader2 className="size-3 animate-spin text-gray-400" aria-hidden />
                ) : null}
                <button
                  type="button"
                  aria-label={`Remove ${att.name}`}
                  className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-zinc-700"
                  onClick={() =>
                    setComposerAttachments((prev) => prev.filter((a) => a.id !== att.id))
                  }
                >
                  <X className="size-3" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex items-end gap-1.5">
          <input
            ref={attachInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.txt,.csv,.md,.doc,.docx"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void addComposerFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            disabled={pending || attachBusy}
            aria-label="Attach files or images"
            title="Attach files or images"
            onClick={() => attachInputRef.current?.click()}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 dark:border-white/10 dark:bg-zinc-800 dark:text-gray-300"
          >
            {attachBusy ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Paperclip className="size-4" aria-hidden />
            )}
          </button>
          <label className="sr-only" htmlFor={`${panelId}-input`}>
            Message One AI
          </label>
          <textarea
            id={`${panelId}-input`}
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendMessage(input);
              }
            }}
            placeholder="Ask One AI…"
            disabled={pending}
            className="max-h-24 min-h-[2.25rem] flex-1 resize-none rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-[#0b3d2e] disabled:opacity-60 dark:border-white/10 dark:bg-zinc-800 dark:text-gray-100"
          />
          <button
            type="submit"
            disabled={
              pending ||
              attachBusy ||
              (!input.trim() && composerAttachments.filter((a) => a.status === "ready").length === 0)
            }
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#0b3d2e] text-white transition-opacity hover:bg-[#0f4d3a] disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0b3d2e] dark:bg-emerald-800"
            aria-label="Send message"
          >
            <Send className="size-4" aria-hidden />
          </button>
        </div>
      </form>
      <p className="border-t border-emerald-900/5 px-3 py-1 text-[10px] text-gray-400 dark:border-white/5 dark:text-gray-500">
        Build {liveVersionLabel}
        {variant === "overlay" ? (
          <span className="ml-2 text-gray-300 dark:text-gray-600">Drag header · resize corner</span>
        ) : null}
      </p>
      {variant === "overlay" ? (
        <button
          type="button"
          aria-label="Resize One AI"
          title="Resize"
          onPointerDown={(e) => startPanelDrag(e, "resize")}
          className="absolute bottom-0 right-0 z-30 size-4 cursor-se-resize rounded-tl bg-emerald-900/20 hover:bg-emerald-900/35 dark:bg-emerald-400/20"
        />
      ) : null}
    </div>
  );

  const headerActions = (
    <div className="flex shrink-0 items-center gap-0.5">
      {mode === "app" ? (
        <>
          <button
            type="button"
            className="rounded-md p-1 text-white/90 transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label="Chat history"
            aria-expanded={historyOpen}
            onClick={() => {
              const next = !historyOpen;
              setHistoryOpen(next);
              if (next) void loadHistory();
            }}
          >
            <History className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            className="rounded-md p-1 text-white/90 transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label="New chat"
            onClick={() => void startNewChat()}
          >
            <Plus className="size-4" aria-hidden />
          </button>
        </>
      ) : null}
      {variant === "overlay" ? (
        <button
          type="button"
          className="rounded-md p-1 text-white/90 transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          aria-label="Close One AI chat"
          onClick={() => setOpen(false)}
        >
          <X className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );

  if (variant === "page") {
    if (!portalReady) return null;
    // Load history sidebar by default on desktop page
    if (!historyOpen && mode === "app") {
      // open history column for page layout without overlay modal semantics
    }
    return (
      <div
        id={panelId}
        className="flex h-[100dvh] w-full flex-col bg-[#f7faf8] dark:bg-zinc-950"
        data-one-ai-page="true"
      >
        <header className="flex shrink-0 items-start justify-between gap-2 border-b border-emerald-900/10 bg-gradient-to-br from-[#0b3d2e] to-[#1a6b4f] px-4 py-3 text-white dark:from-emerald-900 dark:to-emerald-800">
          <div className="min-w-0">
            <p id={titleId} className="flex items-center gap-1.5 text-base font-semibold tracking-tight">
              <Sparkles className="size-4 shrink-0 opacity-90" aria-hidden />
              One AI
            </p>
            <p className="mt-0.5 text-xs leading-snug text-emerald-50/90">{OPENING_LINE}</p>
            {mode === "app" ? (
              <p className="mt-0.5 text-[11px] text-emerald-100/80">On {pageCtx.label}</p>
            ) : null}
          </div>
          {headerActions}
        </header>
        <div className="flex min-h-0 flex-1">
          {mode === "app" ? (
              <div className="hidden h-full w-72 shrink-0 flex-col border-r border-emerald-900/10 bg-white md:flex dark:border-white/10 dark:bg-zinc-950">
                <div className="flex items-center justify-between border-b border-emerald-900/10 px-3 py-2 dark:border-white/10">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    History
                  </p>
                  <button
                    type="button"
                    className="rounded-md px-2 py-1 text-[11px] font-medium text-[#0b3d2e] hover:bg-emerald-50 dark:text-emerald-300"
                    onClick={() => void startNewChat()}
                  >
                    New chat
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {historyLoading ? (
                    <p className="flex items-center gap-1.5 px-2 text-xs text-gray-500">
                      <Loader2 className="size-3.5 animate-spin" aria-hidden />
                      Loading…
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {historyItems.map((item) => (
                        <li key={item.id}>
                          <button
                            type="button"
                            className={`w-full rounded-lg px-2.5 py-2 text-left text-xs transition-colors hover:bg-emerald-50 dark:hover:bg-zinc-800 ${
                              conversationId === item.id
                                ? "bg-emerald-50 ring-1 ring-emerald-800/20 dark:bg-emerald-950/40 dark:ring-emerald-500/30"
                                : ""
                            }`}
                            onClick={() => void openConversation(item.id)}
                          >
                            <span className="block font-medium text-gray-900 dark:text-gray-100">
                              {item.title}
                            </span>
                            {item.preview ? (
                              <span className="mt-0.5 line-clamp-2 block text-[11px] text-gray-500 dark:text-gray-400">
                                {item.preview}
                              </span>
                            ) : null}
                            <span className="mt-0.5 block text-[10px] text-gray-400">
                              {new Date(item.updatedAt).toLocaleString()}
                            </span>
                          </button>
                        </li>
                      ))}
                      {historyItems.length === 0 ? (
                        <li className="px-2 text-xs text-gray-500">No saved chats yet.</li>
                      ) : null}
                    </ul>
                  )}
                </div>
              </div>
          ) : null}
          {chatColumn}
        </div>
      </div>
    );
  }

  // Overlay semantics: portal to body + position:fixed. Do not add `relative` on
  // the panel root (Tailwind .relative overrides .fixed and reflows the page).
  const overlay = (
    <>
      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          style={{
            position: "fixed",
            zIndex: 50,
            left: panelGeo.left,
            top: panelGeo.top,
            width: panelGeo.width,
            height: panelGeo.height,
            margin: 0,
          }}
          className="fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-emerald-900/15 bg-[#f7faf8] shadow-xl dark:border-white/10 dark:bg-zinc-900"
          data-one-ai-overlay="true"
        >
          <header
            className="relative z-20 flex shrink-0 cursor-grab items-start justify-between gap-2 border-b border-emerald-900/10 bg-gradient-to-br from-[#0b3d2e] to-[#1a6b4f] px-3 py-2.5 text-white active:cursor-grabbing dark:from-emerald-900 dark:to-emerald-800"
            onPointerDown={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest("button")) return;
              startPanelDrag(e, "move");
            }}
          >
            <div className="min-w-0 select-none">
              <p id={titleId} className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                <Sparkles className="size-3.5 shrink-0 opacity-90" aria-hidden />
                One AI
              </p>
              <p className="mt-0.5 text-[11px] leading-snug text-emerald-50/90">{OPENING_LINE}</p>
              {mode === "app" ? (
                <p className="mt-0.5 text-[10px] text-emerald-100/80">On {pageCtx.label}</p>
              ) : null}
            </div>
            {headerActions}
          </header>
          {chatColumn}
        </div>
      ) : null}
    </>
  );

  if (!portalReady) return null;
  return createPortal(overlay, document.body);
}

/** Dispatch from utility bar / other shells to open One AI. */
export function openSupportAgentBubble() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SUPPORT_AGENT_OPEN_EVENT));
}

export const openOneAiBubble = openSupportAgentBubble;
